import type { LanguageModelUsage } from 'ai';
import { env } from '../../env';
import type { LoopFact } from '../capabilities/types';
import type { WindowStats } from '../model-window';
import type { AskState, PreRead } from '../preread/compose';
import type { PreReadMode } from '../preread/mode';
import type { PreReadFailure, PreReadOutcome } from '../preread/read';
import type { OutputItemEntry, ProviderTail } from './provider-tail';
import type { ChipMode, FollowUp, MessageRead } from '../turn/follow-up';

// On everywhere except production. Previews keep the logs so a planner change can be
// reviewed before it ships.
export const PLANNER_DEBUG = !env.LIVE;

// Token counts, shortened from 10,000 up: 12345 → 12.3k.
export const tokens = (value: number | undefined): string => {
  if (value == null) return '?';
  return value >= 10_000 ? `${(value / 1000).toFixed(1)}k` : String(value);
};

// One-line JSON with a length cap, so a big payload cannot flood the console.
export const preview = (value: unknown, max = 400): string => {
  let text: string;
  try {
    text = typeof value === 'string' ? value : JSON.stringify(value);
  } catch {
    text = String(value);
  }
  if (!text) return '';
  const line = text.replace(/\s+/g, ' ');
  return line.length > max ? `${line.slice(0, max)}… (${line.length} chars)` : line;
};

// Token counts of one model call, flattened so they can be sent in the stream.
export type PlannerUsageSummary = {
  input?: number;
  output?: number;
  total?: number;
  cacheRead?: number;
  cacheWrite?: number;
  reasoning?: number;
};

export const summariseUsage = (usage: LanguageModelUsage): PlannerUsageSummary => ({
  input: usage.inputTokens,
  output: usage.outputTokens,
  total: usage.totalTokens,
  cacheRead: usage.inputTokenDetails?.cacheReadTokens,
  cacheWrite: usage.inputTokenDetails?.cacheWriteTokens,
  reasoning: usage.outputTokenDetails?.reasoningTokens,
});

// How one model step ended, as the provider reported it.
export type StepFinish = {
  reason: string;
  // The provider's own reason, when it gave one. On OpenAI, the response's incomplete details.
  raw?: string;
  // Only on a step that ended oddly, so the provider can look the response up.
  responseId?: string;
};

// Text or a tool call ends a step normally. The raw reason is only detail: Anthropic sets
// one on every response, OpenAI only on an incomplete one.
export const isUnusualFinish = (finish: StepFinish): boolean =>
  finish.reason !== 'stop' && finish.reason !== 'tool-calls';

// The finish of one SDK step, with the response id kept only when it ended oddly.
export const stepFinishOf = (step: {
  finishReason: string;
  rawFinishReason: string | undefined;
  response: { id: string };
}): StepFinish => {
  const finish: StepFinish = {
    reason: step.finishReason,
    ...(step.rawFinishReason ? { raw: step.rawFinishReason } : {}),
  };
  return isUnusualFinish(finish) ? { ...finish, responseId: step.response.id } : finish;
};

export const formatFinish = ({ reason, raw, responseId }: StepFinish): string =>
  [`finish=${reason}`, raw ? `raw=${raw}` : '', responseId ? `resp=${responseId}` : '']
    .filter(Boolean)
    .join(' ');

export const formatUsage = (usage: PlannerUsageSummary): string => {
  const cache = [
    usage.cacheRead ? `read ${tokens(usage.cacheRead)}` : null,
    usage.cacheWrite ? `write ${tokens(usage.cacheWrite)}` : null,
  ]
    .filter(Boolean)
    .join(' / ');
  return [
    `in=${tokens(usage.input)}`,
    cache ? `(cache ${cache})` : null,
    `out=${tokens(usage.output)}`,
    usage.reasoning ? `reasoning=${tokens(usage.reasoning)}` : null,
    `total=${tokens(usage.total)}`,
  ]
    .filter(Boolean)
    .join(' ');
};

// One tool call as the server saw it, for the end-of-turn summary.
export type PlannerToolCallLog = {
  name: string;
  ms: number;
  ok: boolean;
  // True when the tool has no server executor and the browser resolves it.
  clientResolved?: boolean;
  // Supplier search only: where it ran. Null for a country-wide search. A search already
  // run in the couple's place is not run again next turn.
  searchedArea?: string | null;
  // Supplier search only: the supplier category it searched.
  searchedType?: string;
  // Supplier search only: how many suppliers the results page holds. Null when the search
  // failed or the place is outside coverage.
  searchFound?: number | null;
};

export type PreReadLog = {
  mode: PreReadMode;
  ms: number;
  failure: PreReadFailure | null;
  read?: {
    askedFact: LoopFact | null;
    askState: AskState;
    reply: string | null;
    confidence: number;
    gated: boolean;
    gives: LoopFact[];
    closingKind: PreRead['closingKind'];
    mainAskedFact: LoopFact | null;
    asksCoach: boolean;
    namesVenueToContact: boolean;
    venueContactScore: number;
    asksWhichVenue: boolean;
    pushesBack: boolean;
  };
  // The extractor's reading of the same ask. Absent when the extractor failed.
  extractorAsked?: LoopFact | null;
  // The ask the turn recorded from both readings. Absent when none was recorded.
  recordedAsk?: LoopFact | null;
};

// The pre-read for test captures, next to the extractor's reading of the same ask, so
// the two can be compared on every turn.
export const preReadLog = (
  mode: PreReadMode,
  outcome: PreReadOutcome,
  extractorAsked: LoopFact | null | undefined,
  recordedAsk?: LoopFact | null,
): PreReadLog => ({
  mode,
  ms: outcome.ms,
  failure: outcome.ok ? null : outcome.failure,
  ...(outcome.ok
    ? {
        read: {
          askedFact: outcome.preRead.askedFact,
          askState: outcome.preRead.askState,
          reply: outcome.preRead.reply,
          confidence: outcome.preRead.confidence,
          gated: outcome.preRead.gated,
          closingKind: outcome.preRead.closingKind,
          mainAskedFact: outcome.preRead.mainAskedFact,
          asksCoach: outcome.preRead.asksCoach,
          namesVenueToContact: outcome.preRead.namesVenueToContact,
          venueContactScore: outcome.preRead.venueContactScore,
          asksWhichVenue: outcome.preRead.asksWhichVenue,
          pushesBack: outcome.preRead.pushesBack,
          gives: (Object.keys(outcome.preRead.gives) as LoopFact[]).filter(
            (fact) => outcome.preRead.gives[fact],
          ),
        },
      }
    : {}),
  ...(extractorAsked === undefined ? {} : { extractorAsked }),
  ...(recordedAsk === undefined ? {} : { recordedAsk }),
});

// What the route sends the browser when a turn ends, so the whole turn (model, steps,
// tools, token use) can be read next to the client-side logs.
export type PlannerTurnLog = {
  model: string;
  // How many capabilities are locked and could be offered.
  locked: number;
  // What started the turn: a tapped chip, or the trigger the coach opened on. Absent when
  // the couple typed a message.
  intent?: string;
  // The id of the chip that was tapped. The intent names its tool, the id names the chip.
  chipId?: string;
  steps: number;
  finishReason: string;
  // How each step ended. A raw reason or an unusual finish marks an incomplete response.
  stepFinishes?: StepFinish[];
  usage: PlannerUsageSummary;
  toolCalls: PlannerToolCallLog[];
  // The tools the turn was allowed to call, and the full list they were chosen from.
  boundTools: readonly string[];
  allTools: readonly string[];
  // The thread as the model saw it: messages sent, messages kept, their size, and the first
  // one kept. A cut changes the cached prefix, so this explains a turn with no cache hit.
  window?: WindowStats;
  // The summary before the window: where it ends, how many messages it replaces, whether
  // this turn built it or may have skipped messages, its size, and each list's length.
  summary?: {
    through: string;
    covers: number;
    built: boolean;
    gap: boolean;
    bytes: number;
    suppliers: number;
    actions: number;
    articles: number;
    taskGroups: number;
  };
  // Whether onboarding always finishes on this turn's build. Absent on older turn logs.
  onboardingCompletion?: boolean;
  // The onboarding stage after the turn, and what decided it.
  onboarding?: {
    stage: 'onboarding' | 'exited';
    settled: readonly string[];
    asked: readonly string[];
    // The safety exit's counts after the turn, with onboarding completion on.
    safety?: { refusals: number; quietTurns: number };
  };
  // The second model call every turn makes, to extract facts.
  extract?: { model: string; ms: number; usage: PlannerUsageSummary };
  /**
   * Where the couple waited, in ms from the turn start. `firstTextMs` and `lastTextMs`: first
   * and last reply text, null with no text. `modelEndMs`: the model stream ended, after the
   * hidden chip line. `extractWaitMs`: wait for the extractor after the reply, null when none
   * ran. `finishMs`: this log was written, after the fact writes.
   */
  timing?: {
    firstTextMs: number | null;
    lastTextMs: number | null;
    // Absent on older turn logs.
    modelEndMs?: number | null;
    extractWaitMs: number | null;
    finishMs: number;
  };
  // The provider's stream events after the last visible text, to show what kept the stream
  // open. Null or absent when the reply had no text.
  providerTail?: ProviderTail | null;
  // The message items of the provider's responses, to tell two joined messages apart.
  outputItems?: OutputItemEntry[] | null;
  // The pre-read (a Jev model call before the plan), on a turn that ran one.
  preRead?: PreReadLog;
  // Whether the message asked for help or advice, as the article list reads it.
  adviceAsked?: boolean;
  /**
   * What the plan decided before the reply, to tell a rule that never fired from an
   * instruction the model ignored. `owedRead` is the pending follow-up (a "debt") stored
   * when the request started.
   */
  plan?: {
    deliver: string | null;
    deliverReason: string | null;
    deliveryDropped: boolean;
    offers: { capability: string; key: string | null; kind: string; clarification: boolean }[];
    continuation: boolean;
    owedRead: { capability: string | null; key: string | null; reason: string | null };
    // A capability offered as a chip next to the question, not as the closing line.
    offeredAside: string | null;
    /**
     * Whether the reply could ask one thing and why, with what the typed message read as.
     * `paused`: they asked for fewer questions, now or on an earlier turn.
     */
    followUp?: FollowUp & {
      read: MessageRead | null;
      paused: boolean;
      // The fact the coach's newest reply asked, from the pre-read or its closing line.
      lastAskFact?: LoopFact | null;
      // Which chips could show under the reply, decided apart from the follow-up.
      chipMode?: ChipMode;
      suggestionsPaused?: boolean;
    };
  };
  // The pending follow-up this request computed at its end, and what it stored. `written`
  // is null when the store already had the same one; `error` is set when it threw.
  debt?: {
    accepted: { capability: string; key: string; reason: string } | null;
    written: { capability: string | null; key: string | null; reason: string | null } | null;
    // The write keys sent to the browser. A matching write clears the follow-up.
    writtenKeys: string[];
    error?: string;
  };
};
